import { decode } from 'he';

/**
 * Unescape inline LaTeX math in Markdown.
 *
 * Rationale:
 * Forking the Markdown serializer from `tiptap-markdown` (which injects escape backslashes)
 * was considered, but inline LaTeX is represented as plain text nodes
 * without a dedicated serializer method or node type to customize.
 *
 * Approach:
 * 1. Decode HTML entities (e.g. `&lt;` → `<`).
 * 2. Split on `$` to isolate inline math segments.
 * 3. Restore `\X` escapes using ESCAPE_MAP.
 * 4. Rejoin with `$` to preserve delimiters.
 */

// 1. Build a map of escaped → unescaped
// Note: This ESCAPE_MAP includes EVERY character that Tiptap Markdown's default serializer escapes in inline text
const ESCAPE_MAP = new Map<string, string>([
  ['\\!', '!'],
  ['\\"', '"'],
  ['\\#', '#'],
  ['\\$', '$'],
  ['\\%', '%'],
  ['\\&', '&'],
  ['\\\'', '\''],
  ['\\(', '('],
  ['\\)', ')'],
  ['\\*', '*'],
  ['\\+', '+'],
  ['\\,', ','],
  ['\\-', '-'],
  ['\\.', '.'],
  ['\\/', '/'],
  ['\\:', ':'],
  ['\\;', ';'],
  ['\\<', '<'],
  ['\\=', '='],
  ['\\>', '>'],
  ['\\?', '?'],
  ['\\@', '@'],
  ['\\[', '['],
  ['\\]', ']'],
  ['\\^', '^'],
  ['\\_', '_'],
  ['\\`', '`'],
  ['\\{', '{'],
  ['\\|', '|'],
  ['\\}', '}'],
  ['\\~', '~'],
  ['\\\\', '\\'],
]);

/** Unescape a single inline snippet */
const unescapeLatexExpr = (latexExpr: string): string =>
  [...ESCAPE_MAP].reduce(
    (result, [esc, un]) => result.replaceAll(esc, un),
    latexExpr,
  );

const transformLine = (line: string): string => {
  // Unescapes all LaTeX expressions in a given string
  const latexExpressions = line.match(/\$([^$]*)\$/g) || [];

  return latexExpressions.reduce((result, latexExpr) => {
    const unescapedLatexExpr = unescapeLatexExpr(latexExpr);
    return result.replace(latexExpr, unescapedLatexExpr);
  }, line);
};

/** Process the whole text, only unescaping content between pairs of `$` */
export const unescapeAllLatex = (text: string): string => {
  // Unescapes all LaTeX expressions in a given string
  // Handles newline characters appropriately
  // Decodes HTML entities in the entire string
  const decodedText = decode(text);
  return decodedText
    .split('\n')
    .map((line: string) => line ? transformLine(line) : line)
    .join('\n');
};
